import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
    Select,
    MenuItem,
    Switch,
    FormControl,
    InputLabel,
    Tooltip,
    Slider,
    useTheme
} from "@mui/material";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { CustomCellRendererProps } from "ag-grid-react";
import IconButton from "@mui/material/IconButton";
import {AddCircleOutline, Delete, ArrowBack, Settings} from "@mui/icons-material";
import {useSnackbar} from "notistack";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { urls } from "@routes";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";

interface Integration {
    id: string;
    email: string;
}

interface PageData {
    data: any[];
    integration: Integration;
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
    available_managed_domains: string[];
    spam_threshold: number
    fbl_auto_pause_enabled: boolean
}


export default function AdminGooglePostmasterPage() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();


    const integrationId = searchParams.get("integration_id");
    const [pageData, setPageData] = useState<any>(null);
    const [openAddTxtDialog, setOpenAddTxtDialog] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
    const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

    useEffect(() => {
        if (!integrationId) {
            enqueueSnackbar("Integration ID is missing.", { variant: "error" });
            navigate(-1);
        }
    }, [integrationId]);


    const pageDataQuery = useQuery({
        queryKey: [
            "adminPostmasterDomains",
            integrationId,
            currentPage,
            pageSize,
            searchQuery,
        ],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/google-postmaster/domains/?integration_id=${integrationId}&page=${currentPage}&page_size=${pageSize}&search=${encodeURIComponent(searchQuery)}`
            ),
        enabled: !!integrationId,
        placeholderData: keepPreviousData,
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    const verifyDomainMutation = useMutation({
        mutationKey: ["verifyPostmasterDomain"],
        mutationFn: (payload: { domain: string; integration_id: string }) =>
            authenticateAndPostData(
                "/admin/google-postmaster-verify/domain/",
                payload
            ),
        onSuccess: () => {
            enqueueSnackbar("Domain verification triggered successfully", {
                variant: "success",
            });
            pageDataQuery.refetch(); // refresh table
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(
                error.data?.message || "Failed to verify domain",
                { variant: "error" }
            );
        },
    });

    const DeleteDomainMutation = useMutation({
        mutationKey: ["DeleteDomain"],
        mutationFn: (payload: { domain: string, integration_id: string }) =>
            authenticateAndPostData(
                "/admin/google-postmaster-remove/domain/",
                payload
            ),
        onSuccess: () => {
            enqueueSnackbar("Domain removed successfully", {
                variant: "success",
            });
            pageDataQuery.refetch(); // refresh table
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(
                error.data?.message || "Failed to verify domain",
                { variant: "error" }
            );
        },
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            const data = pageDataQuery.data.data as PageData;
            setPageData(data);
        }
    }, [pageDataQuery.data]);

    const updateSpamThresholdMutation = useMutation({
        mutationKey: ["updateSpamThreshold"],        
        mutationFn: (payload: { threshold: number; autoPause: boolean }) =>
            authenticateAndPostData("/admin/manage-fbl/", {                
                spam_threshold: payload.threshold / 100,
                fbl_auto_pause_enabled: payload.autoPause,
            }),
        onSuccess: (data) => {
            enqueueSnackbar("FBL settings updated successfully", { variant: "success" });
            pageDataQuery.refetch();
            setIsManageDialogOpen(false);
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error.data.message || "Failed to update settings", { variant: "error" });
        }
    });

    function openAddNewDomainDialog(domain?: string) {
        console.log(domain)
        setSelectedDomain(domain ?? null);
        setOpenAddTxtDialog(true);
    }

    function closeAddNewDomainDialog() {
        setSelectedDomain(null);
        setOpenAddTxtDialog(false);
    }
    
    const UserEmailLink = (cellProps: CustomCellRendererProps) => {
        return (
            <Link
                to={urls["adminUserDetails"].replace(":userId", cellProps.data["user_id"])}
                style={{ color: theme.palette.text.primary, textDecoration: "underline" }}
            >
                {cellProps.value}
            </Link>
        );
    };

    // ============================================================
    // --------------------- TABLE COLUMNS ------------------------
    // ============================================================
    const columnDefs: ColDef[] = [
    {
        field: "domain",
        headerName: "Domain",
        flex: 1,
        minWidth: 200,
        cellRenderer: (params: any) => {
            const handleCopy = (e: React.MouseEvent) => {
                e.stopPropagation();
                navigator.clipboard.writeText(params.value);
                // toast.success("Copied!"); 
            };

            return (
                <div className="copy-container" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    width: '100%',
                    height: '100%'
                }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {params.value}
                    </span>
                    <button
                        onClick={handleCopy}
                        className="copy-button"
                        title="Copy to clipboard"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            color: '#666',
                            transition: 'opacity 0.2s ease-in-out'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                    </button>

                    {/* Style tag to handle the hover state locally */}
                    <style>{`
                        .copy-button { opacity: 0; }
                        .copy-container:hover .copy-button { opacity: 1; }
                    `}</style>
                </div>
            );
        },
    },
        {
            field: "email",
            headerName: "User Email",
            flex: 1,
            minWidth: 200,
            cellRenderer: UserEmailLink, // Clicking redirects to admin user details page
        },
        {
            field: "workspace",
            headerName: "Workspace",
            minWidth: 150,
        },
        {
        field: "status",
        headerName: "Postmaster Status",
        minWidth: 140,
        cellRenderer: (params: any) => (
            <span style={{ 
                fontWeight: 500, 
                color: params.value === "Connected" ? "#2e7d32" : "#d32f2f" 
            }}>
                {params.value}
            </span>
        ),
    },
    {
        field: "stats",
        headerName: "Stats",
        minWidth: 150,
        cellRenderer: (params) => (
            <Button
                size="small"
                variant="contained"
                disabled={!params.data.is_verified}
                onClick={() =>
                    navigate(`/admin/google-postmaster-stats/?integration_id=${integrationId}&domain=${params.data.domain}`)
                }
            >
                View Stats
            </Button>
        ),
    },
    //    {
    //     field: "txt",
    //     headerName: "Add / Update TXT",
    //     minWidth: 180,
    //     cellRenderer: (params) => {
    //         console.log(params.data.domain);
    //         return (
    //         <Button
    //             size="small"
    //             variant="outlined"
    //             onClick={() => openAddNewDomainDialog(params.data.domain)}
    //         >
    //             {params.data.txt_added ? "Update TXT" : "Add TXT"}
    //         </Button>
    //         );
    //     },
    //     },
        {
            field: "verify",
            headerName: "Verify",
            minWidth: 140,
            cellRenderer: (params: any) => {
                const isVerified = params.data.is_verified;

                return (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={isVerified || verifyDomainMutation.isPending}
                        onClick={() =>
                            verifyDomainMutation.mutate({
                                domain: params.data.domain,
                                integration_id: integrationId!,
                            })
                        }
                    >
                        {isVerified ? "Verified" : "Verify"}
                    </Button>
                );
            },
        },
        {
            field: "is_verified",
            headerName: "Postmaster",
            minWidth: 140,
            cellRenderer: (params) =>
                params.value ? "Verified" : "Not Verified",
        },
        {
            field: "created_on",
            headerName: "Created On",
            minWidth: 140,
        },
        {
            field: "actions",
            headerName: "Actions",            
            sortable: false,            
            editable: false,
            minWidth: 100,            
            cellRenderer: (params: any) => {
                    const isMapped = params.data.is_mapped;                    
                    
                    // Determine the tooltip message
                    const tooltipTitle = isMapped 
                        ? "Remove this domain from Postmaster" 
                        : "This domain is not yet added to this integration";

                    return (                        
                        <Tooltip title={tooltipTitle} arrow>
                            <span>
                                <IconButton
                                    color="error"
                                    size="small"
                                    disabled={!isMapped}
                                    onClick={() => {
                                        if (window.confirm(`Are you sure you want to remove ${params.data.domain}?`)) {
                                            DeleteDomainMutation.mutate({
                                                domain: params.data.domain,
                                                integration_id: integrationId!
                                            });
                                        }
                                    }}
                                >
                                    <Delete />
                                </IconButton>
                            </span>
                        </Tooltip>
                    );
                }
            }        
    ];

    const ActionButtons = () => {
        return (
            <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={() => setIsManageDialogOpen(true)}
                >
                    Manage FBL
                </Button>
                {/* <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                        navigate(`/admin/google-postmaster-stats/?integration_id=${integrationId}`)
                    }
                >
                    All Domain stats
                </Button> */}
            </Stack>
        );
    };
    
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleSearchChange = (search: string) => {
        setSearchQuery(search);
        setCurrentPage(1); // Reset to first page when searching
    };

    // if (pageDataQuery.isLoading) {
    //         return (
    //             <PageLoading/>
    //     )

    const isInitialLoading =
    pageDataQuery.isLoading && !pageDataQuery.data;

    if (isInitialLoading) { 
        return <PageLoading />;
    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

    } else if (pageData) {
            return (
            <Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
                <Typography fontWeight="bold" variant="h5">
                    
                </Typography>
                <Stack direction={"row"} spacing={2} alignItems={"center"}>
                    <IconButton
                        onClick={() => navigate(urls["adminPostmasterFbl"])}><ArrowBack/></IconButton>
                    <Typography fontWeight={"bold"} variant={"h5"}>Google Postmaster Domains ({pageData.integration.email})</Typography>
                </Stack>
                <Divider />
                <Box sx={{ height: "100%", maxHeight: 800, width: "100%", overflow: "hidden" }}>
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={{
                            data: pageData.data,
                            total_count: pageData.total_count,
                            total_pages: pageData.total_pages,
                            current_page: pageData.current_page,
                            page_size: pageData.page_size,
                        }}
                        loading={pageDataQuery.isLoading || pageDataQuery.isFetching}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search by domain"
                        actions={<ActionButtons />}
                    />                                 
                </Box>

                <AddTXTDomainDialog
                    availableDomains={pageData.available_managed_domains}
                    open={openAddTxtDialog}
                    close={closeAddNewDomainDialog}
                    defaultDomain={selectedDomain}
                    selectedIntegrationId={integrationId}
                    successCallback={() => {
                        enqueueSnackbar("TXT record saved successfully", {
                            variant: "success",
                        });
                        pageDataQuery.refetch();
                        closeAddNewDomainDialog();
                    }}
                    failCallback={(msg) =>
                        enqueueSnackbar(msg, { variant: "error" })
                    }
                />

                <ManageFblDialog 
                    open={isManageDialogOpen}
                    onClose={() => setIsManageDialogOpen(false)}                    
                    currentThreshold={(pageData.spam_threshold ?? 0.003) * 100}
                    isAutoPauseEnabled={pageData.fbl_auto_pause_enabled ?? true}
                    IsLoading={updateSpamThresholdMutation.isPending}
                    // Updated onSave to pass the object directly to mutate
                    onSave={(data: { threshold: number; autoPause: boolean }) => {
                        updateSpamThresholdMutation.mutate(data);
                    }}
                />
            </Stack>
        );
    }
    else {
        // Ideally it should not reach here.
        return <></>;
    }
}


function AddTXTDomainDialog(props: {
    availableDomains: string[];
    open: boolean;
    selectedIntegrationId?: string | null;
    close: () => void;
    defaultDomain?: string | null;
    successCallback: () => void;
    failCallback: (message: string) => void;
}) {
    const [selectedDomain, setSelectedDomain] = useState("");    
    const [txtValue, setTxtValue] = useState("");

    console.log("selectedDomain", selectedDomain)
    console.log("availableDomains", props.availableDomains)

    useEffect(() => {
        if (props.defaultDomain) {
            setSelectedDomain(props.defaultDomain);
        } else {
            setSelectedDomain("");
        }        
    }, [props.defaultDomain, props.open]);

    const addDomainMutation = useMutation({
        mutationKey: ["addOrUpdatePostmasterDomain"],
        mutationFn: () =>
            authenticateAndPostData(
                "/admin/google-postmaster-add-or-update/domain/",
                {
                    domain: selectedDomain,
                    integration_id: props.selectedIntegrationId,
                    txt_value: txtValue,
                }
            ),
        onSuccess: props.successCallback,
        onError: (error: ApiRequestFailed) =>
            props.failCallback(error.data.message),
    });

    return (
        <Dialog open={props.open} maxWidth="md" fullWidth>
            <DialogTitle>Add / Update Domain in Google Postmaster</DialogTitle>

            <DialogContent>
                <Stack spacing={4} sx={{ mt: 2 }}>
                    {/* STEP 1 */}
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            <b>1.</b> Select the domain you want to connect with Google Postmaster.
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Domain</InputLabel>
                            <Select
                                value={selectedDomain}
                                label="Domain"
                                disabled={!!props.defaultDomain}
                                onChange={(e) =>
                                    setSelectedDomain(e.target.value)
                                }
                            >
                                {(props.defaultDomain
                                    ? [props.defaultDomain]
                                    : props.availableDomains
                                ).map((domain) => (
                                    <MenuItem key={domain} value={domain}>
                                        {domain}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    {/* STEP 2 */}
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            <b>2.</b> Visit{" "}
                            <a
                                href="https://postmaster.google.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                https://postmaster.google.com
                            </a>{" "}
                            and add <b>{selectedDomain || "your domain"}</b>.
                            <br />
                            Once added, copy the TXT value shown by Google and
                            paste it below.
                        </Typography>

                        <textarea
                            placeholder="Enter TXT record value..."
                            rows={5}
                            value={txtValue}
                            disabled={!selectedDomain}
                            onChange={(e) => setTxtValue(e.target.value)}                            
                        />                        
                    </Stack>

                    {/* STEP 3 */}
                    <Typography variant="body1">
                        <b>3.</b> After clicking <b>Confirm</b>, go back to Google
                        Postmaster and click <b>Verify</b>.
                    </Typography>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={props.close}>Cancel</Button>

                <Button
                    variant="contained"
                    disabled={
                        !selectedDomain ||
                        !txtValue ||
                        addDomainMutation.isPending
                    }
                    onClick={() => addDomainMutation.mutate()}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}


const ManageFblDialog = ({ open, onClose, currentThreshold, onSave, IsLoading, isAutoPauseEnabled }: any) => {
    const [threshold, setThreshold] = useState(currentThreshold || 0.3);
    const [autoPause, setAutoPause] = useState(isAutoPauseEnabled ?? true);

    useEffect(() => {
        if (open) {
            setThreshold(currentThreshold || 0.3);
            setAutoPause(isAutoPauseEnabled ?? true);
        }
    }, [currentThreshold, isAutoPauseEnabled, open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings /> FBL Management Settings
            </DialogTitle>
            
            <DialogContent dividers>
                {/* 1. Master Toggle for Auto-Pause */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="subtitle2">Enable Auto-Pause</Typography>
                        <Typography variant="caption" color="textSecondary">
                            Pause running campaigns and notify users.
                        </Typography>
                    </Box>
                    <Switch 
                        checked={autoPause} 
                        onChange={(e) => setAutoPause(e.target.checked)} 
                        color="primary"
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 2. Slider Section (Always Enabled) */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                        Global Spam Threshold (%) 
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                        Triggers Spam Bans for users.
                    </Typography>
                    
                    <Box sx={{ px: 2, mt: 4 }}>
                        <Slider
                            value={threshold}
                            min={0.1}
                            max={1.0}
                            step={0.05}
                            valueLabelDisplay="on"
                            onChange={(_, newValue) => setThreshold(newValue as number)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption">Strict (0.1%)</Typography>
                            <Button 
                                size="small" 
                                variant="text" 
                                sx={{ fontSize: '10px', p: 0 }}
                                onClick={() => setThreshold(0.3)}
                            >
                                Reset to 0.3%
                            </Button>
                            <Typography variant="caption">Lenient (1.0%)</Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={IsLoading}>Cancel</Button>
                <Button 
                    variant="contained" 
                    disabled={IsLoading}
                    onClick={() => onSave({ threshold, autoPause })}
                >
                    {IsLoading ? "Saving..." : "Save Changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};